import { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../ui/sheet";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import { Bell, AlertTriangle, CheckCircle2, Mail, MailX } from "lucide-react";
import { api } from "../../lib/api";
import { useT } from "../../lib/i18n";

export default function AlarmEventsSheet({ open, onOpenChange, alarm }) {
  const t = useT();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !alarm?.id) return;
    setLoading(true);
    api
      .get(`/alarms/${alarm.id}/events`)
      .then(({ data }) => setEvents(data || []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [open, alarm?.id]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md" data-testid="alarm-events-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-primary" />
            {t("alarm.history")}
          </SheetTitle>
          <SheetDescription>
            {alarm ? (
              <>
                <span className="font-medium">{alarm.name}</span> — {t("alarm.value")}{" "}
                <span className="font-mono">
                  {alarm.condition === "gt" ? ">" : "<"} {alarm.threshold}
                </span>
                , {t("alarm.every_n_min", { n: alarm.interval_minutes })}
              </>
            ) : null}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6">
          {loading ? (
            <div className="text-sm text-muted-foreground">{t("common.loading")}</div>
          ) : events.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm flex flex-col items-center">
              <CheckCircle2 className="h-6 w-6 mb-2 text-emerald-500" />
              {t("alarm.no_events")}
            </div>
          ) : (
            <ScrollArea className="h-[calc(100vh-12rem)] pr-3">
              <div className="space-y-3">
                {events.map((ev, idx) => {
                  const ts = new Date(ev.created_at);
                  return (
                    <div
                      key={ev.id || idx}
                      className="card-soft p-3"
                      data-testid={`alarm-event-${ev.id || idx}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="flex items-center gap-2">
                            <Badge
                              variant="outline"
                              className="border-destructive/30 text-destructive bg-destructive/10"
                            >
                              <AlertTriangle className="h-3 w-3 mr-1" /> {t("alarm.triggered")}
                            </Badge>
                            {ev.notified ? (
                              <Badge variant="outline" className="border-emerald-500/30 text-emerald-600 dark:text-emerald-400 bg-emerald-500/10">
                                <Mail className="h-3 w-3 mr-1" /> {t("alarm.email_sent")}
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-muted-foreground">
                                <MailX className="h-3 w-3 mr-1" /> {t("alarm.not_notified")}
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1.5">
                            {ts.toLocaleString()}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="kpi-label">{t("alarm.value")}</div>
                          <div className="font-mono font-semibold">{ev.value}</div>
                        </div>
                      </div>
                      {ev.notify_status && !ev.notified && (
                        <div className="mt-2 text-[11px] font-mono text-muted-foreground border-t pt-2">
                          {ev.notify_status}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
